import React, { useState, useEffect, useRef } from "react";
import './HolidayManagement.css';
import Select from "react-select";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createNewHoliday, deleteHoliday, getAllHolidays, updateHolidays } from "../../../services/leaveService";
import { selectStyles } from "../../../assets/Styles/selectStyle";
import { getStates } from "../../../services/monthlyAttendanceService";
import { capitalizeFirstLetter } from "../../../utils/validations";
import { format, isAfter, startOfDay, addDays } from 'date-fns';
import { Navigate, useNavigate } from "react-router-dom";
import { getAuditLog } from "../../../utils/calculateDaysInMonthUtils";

const HolidayManagement = () => {
  const [tableData, setTableData] = useState([]); // To store table data
  const [holidayName, setHolidayName] = useState(''); // Holiday name input
  const [date, setDate] = useState(null); // Holiday date input
  const [holidayType, setHolidayType] = useState(''); // Holiday type input
  const [description, setDescription] = useState(''); // Description input
  const [editHolidayId, setEditHolidayId] = useState(null); // Store the ID of the holiday being edited
  const [showForm, setShowForm] = useState(false); // Show or hide the form
  const [error, setError] = useState(''); // Error message state for invalid date
  const dateInputRef = useRef(null); // Ref to the date input field
  const [selectedYear, setSelectedYear] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [state, setState] = useState(3);
  const navigate = useNavigate();

  // Holiday type options
  const isFormValid = holidayName.trim() !== '' && date instanceof Date && !isNaN(date) && state !== null;
  const holidayTypeOptions = [
    { value: "Public holiday", label: "Public holiday" },
    { value: "Festival holiday", label: "Festival holiday" },
    { value: "Company holiday", label: "Company holiday" },
  ];

  const fetchHolidays = async () => {
    try {
      const year = selectedYear.getFullYear(); // Extract year from Date object
      const response = await getAllHolidays(state, year);
      if (response.status === 200) {
        const sortedData = response.data.sort((a, b) => new Date(a.date) - new Date(b.date));
        setTableData(sortedData);
      } else {
        console.log("Failed to fetch holidays.");
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      alert("There was an error fetching the holiday data.");
    }
  };

  useEffect(() => {
    if (state && selectedYear) {
      fetchHolidays();
    }
  }, [state, selectedYear]);

  const fetchStates = async () => {
    try {
      const data = await getStates();
      setStateOptions(data.map(item => ({
        value: item.id,
        label: capitalizeFirstLetter(item.stateName),
      })));
    } catch (err) {
      console.error('Error fetching states:', err);
      setError(prev => ({ ...prev, general: 'Failed to load states' }));
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  // Helper function to check if a date is tomorrow or later
  const isDateEditable = (holidayDate) => {
    const today = startOfDay(new Date()); // Current date at start of day
    const tomorrow = addDays(today, 1); // Tomorrow's date
    const parsedHolidayDate = new Date(holidayDate); // Parse holiday date
    return isAfter(parsedHolidayDate, today); // True if holiday is tomorrow or later
  };

  // Handle Delete Holiday
  const handleDeleteHoliday = async (id) => {
    const holidayId = parseInt(id, 10);
    if (isNaN(holidayId)) {
      console.error("Error: ID is invalid.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this holiday?")) {
      return;
    }

    try {
      await deleteHoliday(holidayId);
      alert("Holiday deleted successfully!");
      setTableData((prevData) => prevData.filter((data) => data.id !== holidayId));
    } catch (error) {
      console.error("Error deleting the holiday:", error);
      alert("Failed to delete the holiday.");
    }
  };

  // Handle Edit Holiday
  const handleEditHoliday = (id) => {
    const holiday = tableData.find((holiday) => holiday.id === id);
    if (holiday) {
      setHolidayName(holiday.holidayName);
      setDate(new Date(holiday.date)); // Ensure date is parsed as Date object
      setHolidayType(holiday.holidayType);
      setDescription(holiday.description);
      setEditHolidayId(id); // Store the ID of the holiday being edited
      setShowForm(true); // Show the form for editing
    }
  };

  // Handle Submit or Update
const handleSubmit = async (event) => {
    event.preventDefault();

    // Clear previous errors
    setError('');

    // Validate input fields
    if (!holidayName || !date) {
      setError('Please fill in all required fields.');
      return;
    }

    const holidayData = {
      holidayName,
      date: format(date, 'yyyy-MM-dd'),
      description,
      stateId: state,
      auditlog: getAuditLog(),
    };

    try {
      if (editHolidayId) {
        const response = await updateHolidays(editHolidayId, holidayData);
        if (response.status === 200) {
          alert("Holiday updated successfully!");
          fetchHolidays();
          setEditHolidayId(null);
          setHolidayName('');
          setDate(null);
          setHolidayType('');
          setDescription('');
          setShowForm(false);
        } else {
          console.log("Failed to update holiday.");
          setError("Failed to update holiday.");
        }
      } else {
        const response = await createNewHoliday(holidayData);
        if (response.status === 200 || response.status === 201) {
          alert("Holiday added successfully!");
          fetchHolidays();
          setHolidayName('');
          setDate(null);
          setHolidayType('');
          setDescription('');
          setShowForm(false);
        } else {
          console.log("Failed to add holiday. Response:", response);
          setError("Failed to add holiday.");
        }
      }
    } catch (error) {
      console.error("Error submitting holiday:", error);
      console.log("Error response:", error.response);

      if (error.response && error.response.status === 400) {
        alert(error.response.data.error || 'An error occurred while creating the holiday.');
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  };

  // Handle Add/Edit Holiday Form Toggle
  const handleAddHoliday = () => {
    setShowForm(!showForm); // Toggle form visibility
    if (showForm) {
      // Reset form when closing
      setHolidayName('');
      setDate(null);
      setHolidayType('');
      setDescription('');
      setEditHolidayId(null);
    }
  };

  // Handle Date Change and Disable Sundays
  const handleDateChange = (selectedDate) => {
    if (!selectedDate) {
      setDate(null);
      setError('');
      return;
    }

    const dayOfWeek = selectedDate.getDay(); // Local day of week: 0 = Sunday

    if (dayOfWeek === 0) {
      setError('Sundays are not allowed as holiday dates.');
      setDate(null);
    } else {
      setError('');
      setDate(selectedDate);
    }
  };

  const handleClearForm = () => {
    setHolidayName('');
    setDate(null);
    setHolidayType('');
    setDescription('');
    setState(null);
    setSelectedYear('');
    setTableData('');

  };

  const gotoDashBoard = () => {
    navigate("/dashboard");
  };

  return (
    <div className="hoildaymanagement-container">
      <div className="holidaymangement-content">
        <h2 className="heading-1">Holiday Management</h2>
        <h4 className="heading-2">{editHolidayId === null ? "Add New Holiday" : "Edit Holiday"}</h4>

        <form onSubmit={handleSubmit} className="holidaymanage-form">
          <div className="holiday-flex">
            <div className={`holiday-floating-label-container ${state ? 'filled' : ''}`}>
              <Select
                name="state"
                options={stateOptions}
                value={stateOptions.find(opt => opt.value === state) || null}
                onChange={(selectedOption) => setState(selectedOption ? selectedOption.value : null)}
                placeholder=" "
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
              <label><span className="required">*</span>State</label>
            </div>
            <div className={`holiday-floating-label-container ${selectedYear ? 'filled' : ''}`}>
              <DatePicker
                selected={selectedYear}
                onChange={(date) => setSelectedYear(date)}
                dateFormat="yyyy"
                showYearPicker
                placeholderText=""
                isClearable
                yearItemNumber={12}
              />
              <label><span className="required">*</span>Select a Year</label>
            </div>
          </div>
          <div className="holiday-button">
            {!showForm && (
              <button type="button" onClick={handleAddHoliday}>
                Add Holiday
              </button>
            )}
          </div>

          {showForm && (
            <>
              <div className="holiday-flex">
                <div className={`holiday-floating-label-container ${date ? 'filled' : ''}`}>
                  <DatePicker
                    selected={date}
                    onChange={handleDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText=" "
                    className="custom-datepicker"
                    // minDate={new Date()}
                    required
                  />
                  <label><span className="required">*</span>Holiday Date</label>
                  {error && <div className="error-message">{error}</div>}
                </div>
                <div className={`holiday-floating-label-container ${holidayName ? 'filled' : ''}`}>
                  <input
                    type="text"
                    value={holidayName}
                    onChange={(e) => setHolidayName(e.target.value)}
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Holiday Name</label>
                </div>
              </div>
              <div className="holiday-flex">
                <div className={`holiday-floating-label-container ${description ? 'filled' : ''}`}>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder=" "
                  />
                  <label>Description (optional)</label>
                </div>
                <div className={`holiday-floating-label-container ${holidayName ? 'filled' : ''}`}></div>
              </div>
            </>
          )}

          <div className="btnss-flex">
            <div className="holiday-buttons">
              <button type="submit" disabled={!isFormValid} className="holiday-button-submit">
                {editHolidayId === null ? "Submit" : "Update"}
              </button>
            </div>
            <div className="holiday-buttons">
              <button type="button" className="holiday-button-clear" onClick={handleClearForm}>
                Clear
              </button>
            </div>
            <div className="holiday-buttons">
              <button type="button" className="holiday-button-close" onClick={gotoDashBoard}>
                Close
              </button>
            </div>
          </div>
        </form>

        {/* Display Holiday Data in a Table */}
        <div>
          <h3 className="holidaytable-heading">Holiday List</h3>
          <div className="holiday-table">
            <table className="holidaymanage-table">
              <thead>
                <tr>
                  <th>S.no</th>
                  <th>Holiday Name</th>
                  <th>Holiday Date</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((data, index) => (
                    <tr key={data.id}>
                      <td>{index + 1}</td>
                      <td>{data.holidayName}</td>
                      <td>{format(new Date(data.date), 'dd-MM-yyyy')}</td>
                      <td>{data.description}</td>
                      <td>
                        <div className="table-buttons">
                          {isDateEditable(data.date) && (
                            <>
                              <button
                                className="holiday-edit"
                                onClick={() => handleEditHoliday(data.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="holiday-delete"
                                onClick={() => handleDeleteHoliday(data.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="5">No records to display</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayManagement;